<div class="help">
  <p class="help-intro">
    This dashboard shows your budget, what has been spent, and what you have left. It has two parts:
    <strong>My Spending Plan</strong> and <strong>Approved Authorizations</strong>.
  </p>
  <p>To open this guide at any time, click the blue &ldquo;i&rdquo; icon next to the dates.</p>

  <section class="help-part">
    <h3 class="help-part-title"><span class="help-part-num">1</span> My Spending Plan</h3>

    <h4>Pick your dates</h4>
    <p>
      At the top, choose a start date and an end date. You can pick any dates you want. Then click
      <strong>Apply Filter</strong>.
    </p>
    <p>
      The dashboard shows all budget lines that are active at any time during those dates. You see the full
      budget for each of those lines, even if a line starts before or ends after your dates.
    </p>
    <div class="help-callout">
      <strong>Good to know:</strong> Your dates change which budget lines you see. They also change the
      <strong>Spent</strong> amount at the top and the <strong>Monthly Breakdown</strong>, which show spending
      only for the dates you picked. <strong>Available to Use</strong> is not limited to your dates: it counts
      all spending on those budget lines.
    </div>
    <div class="help-callout help-tip">
      <strong>Tip:</strong> When you first open the page, both dates are set to today. To see more of your
      plan, change the start and end dates.
    </div>

    <h4>Narrow down what you see</h4>
    <p>You can also filter by:</p>
    <ul>
      <li>Spending Plan name</li>
      <li>Provider. You see the provider&rsquo;s name. Hover over a name to see the provider ID.</li>
      <li>Category</li>
      <li>
        Service Code. The list shows the service code and its description. If you pick a category first, the
        list only shows services in that category.
      </li>
    </ul>
    <p>
      Click <strong>Apply Filter</strong> to see your changes. Click <strong>Reset Filters</strong> to go
      back to today&rsquo;s date with no filters.
    </p>
    <div class="help-callout">
      <strong>Good to know:</strong> Your filters change everything on the page except the Approved
      Authorizations table at the bottom.
    </div>

    <h4>What the big numbers mean</h4>
    <dl class="help-terms">
      <dt>Available to Use</dt>
      <dd>
        The money you have left on the budget lines you see. It counts everything spent on them, even spending
        outside the dates you picked.
      </dd>
      <dt>Budgeted</dt>
      <dd>The total money set aside for the budget lines you see.</dd>
      <dt>Spent</dt>
      <dd>
        The money spent within your dates. This includes timesheets and invoices that are paid, good to pay,
        or waiting for approval, and purchase requests as soon as they are made.
      </dd>
      <dt>Used</dt>
      <dd>
        The percent of your budget that has been spent (paid, good to pay, or waiting for approval). The bar
        next to it fills up as you spend. A note under it shows how much was spent this month.
      </dd>
    </dl>
    <div class="help-callout">
      <strong>Good to know:</strong> &ldquo;Good to pay&rdquo; means a timesheet or invoice is approved and
      ready to be paid, but has not been paid yet. Available to Use can be less than Budgeted minus Spent. This
      is usually because of spending outside the dates you picked. If a timesheet, invoice or purchase request
      is rejected or denied, its amount comes back to your balance.
    </div>

    <h4>By Category</h4>
    <p>
      This shows how much has been spent from the budget for each category, such as Living Arrangement. It
      also shows the percent used.
    </p>

    <h4>View allocation details</h4>
    <p>
      Click <strong>View allocation details</strong> under the big number. A window called
      <strong>My Spending Plan Details</strong> opens. It has a table with each category and service code. For
      each line, it shows:
    </p>
    <dl class="help-terms">
      <dt>Budgeted</dt>
      <dd>The money set aside</dd>
      <dt>Spent</dt>
      <dd>The money spent within your dates</dd>
      <dt>Remaining</dt>
      <dd>The money left, after taking out everything spent, even outside your dates</dd>
    </dl>
    <p>You also see a total for each category and a <strong>Total Budget</strong> at the bottom.</p>

    <h4>Monthly Breakdown</h4>
    <p>
      This chart is right on the main page. It shows how much was spent each month in your dates. Each color
      is a different category. Hover over a month to see the exact amount for each category and the total for
      that month.
    </p>
    <div class="help-callout">
      <strong>Good to know:</strong> The chart counts timesheets and invoices that are paid, good to pay, or
      waiting for approval. If one is rejected or denied, it comes off the chart.
    </div>

    <h4>Budget lines table</h4>
    <p>
      The table under the chart lists each budget line. It shows the amount budgeted and how much was spent in
      each month.
    </p>
    <p>Click <strong>Export to excel</strong> to download this table as a spreadsheet.</p>
  </section>

  <section class="help-part">
    <h3 class="help-part-title"><span class="help-part-num">2</span> Approved Authorizations</h3>
    <p>This table lists all of your approved authorizations.</p>
    <div class="help-callout">
      <strong>Good to know:</strong> The dates and filters at the top of the page do not change this table.
      You will always see all approved authorizations.
    </div>

    <h4>Find a specific authorization</h4>
    <p>
      Under each column name is a small box. Type in a box to filter that column. For example, type a service
      code to see only authorizations with that code.
    </p>

    <h4>What the columns mean</h4>
    <dl class="help-terms">
      <dt>Authorized Dollars</dt>
      <dd>The money approved for this authorization</dd>
      <dt>Spent Dollars</dt>
      <dd>
        The money used from timesheets and invoices for this authorization. This includes amounts that are
        paid, good to pay, or waiting for approval. It covers the whole authorization, not just the dates at
        the top.
      </dd>
      <dt>Remaining Dollars</dt>
      <dd>Authorized Dollars minus Spent Dollars</dd>
    </dl>

    <h4>See more columns</h4>
    <p>This table is wide. Scroll to the right to see more columns, like the start date, end date, and status.</p>

    <h4>See the details of an authorization</h4>
    <p>
      Click <strong>View</strong> at the end of any row to see all the details of that authorization.
      <strong>Spent Dollar Details</strong> lists each timesheet and invoice behind the amount spent, with its
      service date.
    </p>
  </section>
</div>
